/**
 * Splash-1a template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';
import marquee from 'vanilla-marquee';
import SimpleParallax from "simple-parallax-js/vanilla";
import KeenSlider from 'keen-slider';
import Player from '@vimeo/player';
import './partials/InteractiveMap';

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Splash-1a';

const numberDoms = document.querySelectorAll(`[data-count-up-number]`);

const neighborhoodMarqueeDom = document.querySelector(`[data-neighborhood-marquee]`);

const parallaxImageDoms = document.querySelectorAll(`[data-parallax]`);

const slideshowDoms = document.querySelectorAll(`[data-gallery-slideshow]`);
const slideshowPrevButtonDom = document.querySelector(`[data-gallery-slideshow-arrow-prev]`);
const slideshowNextButtonDom = document.querySelector(`[data-gallery-slideshow-arrow-next]`);

const yborVideoTriggerDom = document.querySelector(`[data-ybor-video-trigger]`);
const yborVideoOverlayDom = document.querySelector(`[data-ybor-video-overlay]`);

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.log(`${NAME} > loaded`);
	initStaggerReveal();
	// initMarquee();
	initNumbers();
	initParallax();
	initSlideshows();
	initYborVideo();
}

/**
 * initSlideshows - Initialize the slideshows
 */
const initSlideshows = () => {
	Logger.log(`${NAME} > initSlideshows`);

	// Check if the slideshows exist
	if (!slideshowDoms) {
		return;
	}

	slideshowDoms.forEach((slideshow) => {

		// Get a random number of milliseconds
		const getRandomInt = (min, max) => {
			return Math.floor(Math.random() * (max - min + 1)) + min;
		};

		// Get the dots
		const dotsDataAttr = `data-gallery-slideshow-control`;
		const dotsActiveClass = `splash-neighborhood__gallery-slideshow-control--active`;
		const dots = slideshow.querySelectorAll(`[${dotsDataAttr}]`);

		// Initialize the slideshow
		const slider = new KeenSlider(
			slideshow,
			{
				selector: `[data-gallery-slideshow-image]`,
				loop: true,
				defaultAnimation: {
					duration: 2000,
				},
				created: () => {
					// console.log('created')
				},
			},
			[
				(slider) => {
					let timeout
					let mouseOver = false
					function clearNextTimeout() {
						clearTimeout(timeout)
					}
					function nextTimeout(s) {

						clearTimeout(timeout)

						// if (mouseOver) return

						timeout = setTimeout(() => {
							slider.next()
						}, 3000);
					}
					slider.on("created", () => {
						slider.container.addEventListener("mouseover", () => {
							mouseOver = true
							clearNextTimeout()
						})
						slider.container.addEventListener("mouseout", () => {
							mouseOver = false
							nextTimeout()
						})
						nextTimeout()

						if (slideshowNextButtonDom) {
							slideshowNextButtonDom.addEventListener("click", () => {
								clearNextTimeout()
								slider.next()
							});
						}

						if (slideshowPrevButtonDom) {
							slideshowPrevButtonDom.addEventListener("click", () => {
								clearNextTimeout()
								slider.prev()
							});
						}
					})
					slider.on("dragStarted", clearNextTimeout)
					slider.on("animationEnded", nextTimeout)
					slider.on("updated", nextTimeout)
					slider.on("slideChanged", (s) => {
						const rel = s.track.details.rel;
						if (dots) {
							dots.forEach((dot, idx) => {
								dot.classList.remove(dotsActiveClass);
								if (idx === rel) {
									dot.classList.add(dotsActiveClass);
								}
							});
						}
					});
				},
			]
		);
	});
}

/**
 * initStaggerReveal - Initialize the image reveal
 */
const initStaggerReveal = () => {
	Logger.log(`${NAME} > initStaggerReveal`);

	gsap.set(`[data-stagger-reveal]`, { opacity: 0, y: 100 });

	ScrollTrigger.batch(`[data-stagger-reveal]`, {
		onEnter: (batch) =>
			gsap.to(batch, {
				duration: 0.75,
				opacity: 1,
				y: 0,
				stagger: 0.3
			}),
		start: "top 95%"
	});

}

/**
 * initMarquee - Initialize the marquee
 */
const initMarquee = () => {
	Logger.log(`${NAME} > initMarquee`);

	// Check if the marquee exists
	if (!neighborhoodMarqueeDom) {
		return;
	}

	// Initialize the marquee
	new marquee(neighborhoodMarqueeDom, {
		duplicated: true,
		gap: 20,
		speed: 100,
		startVisible: true,
	});
}

/**
 * initNumbers - Initialize the numbers
 * https://jshakespeare.com/simple-count-up-number-animation-javascript-react/
 */
const initNumbers = () => {
	Logger.info(`${NAME} > initNumbers`);

	if (!numberDoms) return;

	// How long you want the animation to take, in ms
	const animationDuration = 3000;

	// Calculate how long each ‘frame’ should last if we want to update the animation 60 times per second
	const frameDuration = 1000 / 60;

	// Use that to calculate how many frames we need to complete the animation
	const totalFrames = Math.round(animationDuration / frameDuration);

	// An ease-out function that slows the count as it progresses
	const easeOutQuad = t => t * (2 - t);

	// The animation function, which takes an Element
	const animateCountUp = el => {
		let frame = 0;
		const countTo = parseInt(el.getAttribute(`data-count-up-number`), 10);

		// Start the animation running 60 times per second
		const counter = setInterval(() => {
			frame++;
			// Calculate our progress as a value between 0 and 1
			// Pass that value to our easing function to get our
			// progress on a curve
			const progress = easeOutQuad(frame / totalFrames);
			// Use the progress value to calculate the current count
			const currentCount = Math.round(countTo * progress);

			// If the current count has changed, update the element
			if (parseInt(el.innerHTML, 10) !== currentCount) {
				el.innerHTML = currentCount.toLocaleString();
			}

			// If we’ve reached our last frame, stop the animation
			if (frame === totalFrames) {
				clearInterval(counter);
			}
		}, frameDuration);
	};


	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const intersecting = entry.isIntersecting;
				if (intersecting && entry.target.innerHTML === '0') {
					animateCountUp(entry.target);
				}
			}
		},
		// 👇 Threshold is 100%
		{ threshold: 1 }
	);

	numberDoms.forEach((dom) => {
		observer.observe(dom);
	});

}

/**
 * initParallax - Initialize the parallax
 */
const initParallax = () => {
	Logger.log(`${NAME} > initParallax`);

	// Check if the parallax exists
	if (!parallaxImageDoms) {
		return;
	}

	// Initialize the parallax
	new SimpleParallax(parallaxImageDoms, {
		delay: .6,
		scale: 1.2,
		transition: 'cubic-bezier(0,0,0,1)',
	});
}

/**
 * initYborVideo - Initialize the Ybor video
 */
const initYborVideo = () => {
	Logger.log(`${NAME} > initYborVideo`);

	// Check if the video trigger exists
	if (!yborVideoTriggerDom) {
		return;
	}

	// Check if the video overlay exists
	if (!yborVideoOverlayDom) {
		return;
	}

	// Check if the video iframe exists
	const videoIframeDom = yborVideoOverlayDom.querySelector(`iframe`);
	if (!videoIframeDom) {
		return;
	}

	// Initialize the video player
	const player = new Player(videoIframeDom, {});

	// Initialize the video
	yborVideoTriggerDom.addEventListener(`click`, (e) => {
		e.preventDefault();
		yborVideoOverlayDom.classList.add(`splash-ybor__overlay--active`);
		player.play();
	});

	yborVideoOverlayDom.addEventListener(`click`, (e) => {
		e.preventDefault();
		yborVideoOverlayDom.classList.remove(`splash-ybor__overlay--active`);
		player.pause();
		player.setCurrentTime(0);
	});

}


// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
